/**
 * ChartInfoDetail - Renders chart info fields with EditableField for editable ones
 */

import React from 'react';
import { JatakaResponse } from '../../../../../models/api';
import { AccordionItem } from '../types';
import { EditableField } from './EditableField';
import { TimeHistoryExpander } from './TimeHistoryExpander';
import { PlaceExpander } from './PlaceExpander';
import { BasicInfo, formatCoordinatesDisplay } from './chartInfoUtils';
import { formatDateFromISO } from '../../../../../config/dateTimeFormats';

export interface ChartInfoDetailProps {
    item: AccordionItem;
    apiResponse?: JatakaResponse | null;
    shareMode: boolean;
    chartId: string;
}

export const ChartInfoDetail: React.FC<ChartInfoDetailProps> = React.memo(({
    item,
    apiResponse,
    shareMode,
    chartId
}) => {
    const basicInfo = item.metadata?.basicInfo as BasicInfo | undefined;
    const fieldVisibility = item.metadata?.fieldVisibility as Record<string, boolean> | undefined;

    if (!basicInfo) {
        return (
            <div className="planet-detail-container">
                <div className="planet-detail-content">
                    {item.details.map((detail, index) => (
                        <div key={index} className="planet-detail-row">
                            <span className="planet-detail-label">{detail.label}:</span>
                            <span className="planet-detail-value">{detail.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="planet-detail-container">
            <div className="planet-detail-content">
                {/* 1. Name - editable */}
                {fieldVisibility?.chart_name && (
                    <EditableField
                        chartId={chartId}
                        label="Name"
                        fieldKey="chart_name"
                        initialValue={basicInfo.chart_name || ''}
                        disabled={shareMode}
                        getValuePath={(resp) => resp.basic_info?.chart_name || ''}
                        setValueInResponse={(resp, value) => ({
                            ...resp,
                            basic_info: { ...resp.basic_info, chart_name: value }
                        })}
                        updateMetadata={(chartInstance, value) => ({
                            ...chartInstance.metadata,
                            name: value,
                            modified: new Date()
                        })}
                    />
                )}

                {/* 2. Date - read only, formatted from datetime_iso */}
                {fieldVisibility?.date && (basicInfo.datetime_iso || basicInfo.date) && (
                    <div className="planet-detail-row">
                        <span className="planet-detail-label">Date:</span>
                        <span className="planet-detail-value">
                            {basicInfo.datetime_iso
                                ? formatDateFromISO(basicInfo.datetime_iso)
                                : basicInfo.date}
                        </span>
                    </div>
                )}

                {/* 3. Time + Timezone - expandable with history and movement settings */}
                {fieldVisibility?.time && basicInfo.datetime_iso && (
                    <TimeHistoryExpander
                        chartId={chartId}
                        datetimeIso={basicInfo.datetime_iso}
                        disabled={shareMode}
                    />
                )}

                {/* 4. Place - editable with Location in expandable subsection */}
                {fieldVisibility?.place_name && (
                    <PlaceExpander
                        chartId={chartId}
                        placeName={basicInfo.place_name || ''}
                        location={fieldVisibility?.location
                            ? (basicInfo.latitudeValue && basicInfo.longitudeValue
                                ? formatCoordinatesDisplay(basicInfo.latitudeValue, basicInfo.longitudeValue)
                                : basicInfo.location)
                            : undefined}
                        disabled={shareMode}
                    />
                )}

                {/* 6. Altitude - read only, hide if 0 */}
                {fieldVisibility?.altitude && basicInfo.altitude &&
                 parseFloat(basicInfo.altitude) !== 0 && (
                    <div className="planet-detail-row">
                        <span className="planet-detail-label">Altitude:</span>
                        <span className="planet-detail-value">{basicInfo.altitude}</span>
                    </div>
                )}
            </div>
        </div>
    );
});
